import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
  Car, 
  Users, 
  Building2, 
  LayoutDashboard, 
  Menu, 
  Bell, 
  Settings,
  MessageCircle,
  Mail,
  Search,
  LayoutGrid,
  LogOut,
  UserCog
} from 'lucide-react';
import { 
  Sheet, 
  SheetContent, 
  SheetTrigger 
} from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '../components/ui/tooltip';
import { NavItem } from '../components/ui/nav';
import LanguageSelector from '../components/LanguageSelector.jsx';

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);

  // Navegação com traduções
  const navigation = [
    { name: t('navigation.vehicles'), href: '/admin/veiculos', icon: <Car className="h-5 w-5" /> },
    { name: t('navigation.buyers'), href: '/admin/compradores', icon: <Users className="h-5 w-5" /> },
    { name: t('navigation.lessors'), href: '/admin/locadoras', icon: <Building2 className="h-5 w-5" />, masterOnly: true },
    { name: t('navigation.kanban'), href: '/admin/kanban', icon: <LayoutDashboard className="h-5 w-5" /> },
    { name: t('navigation.searchVehicle'), href: '/admin/veiculos/search', icon: <Search className="h-5 w-5" />, masterOnly: true },
    { name: t('navigation.whatsapp'), href: '/admin/whatsapp', icon: <MessageCircle className="h-5 w-5" /> },
    { name: t('navigation.contacts'), href: '/admin/contatos', icon: <Mail className="h-5 w-5" /> },
    { name: t('navigation.users'), href: '/admin/usuarios', icon: <UserCog className="h-5 w-5" /> },
    { name: t('navigation.catalog'), href: '/', icon: <LayoutGrid className="h-5 w-5" />, external: true, masterOnly: true },
  ];

  const handleLogout = () => {
    console.log('Fazendo logout...');
    logout();
    navigate('/login', { replace: true });
  };

  // Gerar iniciais do usuário
  const getUserInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .slice(0, 2)
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase();
  };

  const SidebarContent = () => {
    const isMaster = user?.role === 'master';
    
    return (
      <div className="h-full">
        <div className="px-6 py-5 flex justify-start">
          <img 
            src="/logo2.svg" 
            alt="Seminovos Logo" 
            className="h-auto max-w-[150px] object-contain" 
          />
        </div>
        <div className="p-4 space-y-2">
          {navigation
            .filter(item => !item.masterOnly || isMaster)
            .map((item) => (
              <NavItem 
                key={item.name}
                href={item.href}
                icon={item.icon}
                isActive={!item.external && (
                  item.href === '/admin/veiculos' 
                    ? location.pathname === '/admin/veiculos' || (
                        location.pathname.startsWith('/admin/veiculos/') && 
                        !location.pathname.includes('/search')
                      )
                    : location.pathname.startsWith(item.href)
                )}
                external={item.external}
              >
                {item.name}
              </NavItem>
            ))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex min-h-screen">
      {/* Sidebar para desktop */}
      <div className="hidden md:block w-[280px] bg-[#F8FAFC]">
        <SidebarContent />
      </div>

      {/* Conteúdo principal */}
      <div className="flex-1 bg-[#F8FAFC]">
        {/* Navbar */}
        <header className="sticky top-0 z-40 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex h-16 items-center px-4 md:px-6">
            <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] p-0 bg-[#F8FAFC]">
                <SidebarContent />
              </SheetContent>
            </Sheet>
            
            <div className="flex-1" />
            
            <div className="flex items-center space-x-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <Bell className="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('common.notifications')}</TooltipContent>
                </Tooltip>

                {/* Seletor de idioma */}
                <LanguageSelector />

                {/* Menu de Configurações */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowSettingsMenu(!showSettingsMenu);
                      }}
                    >
                      <Settings className="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('common.settings')}</TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Menu do usuário */}
              <div className="relative">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="ghost" 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Clicou no menu do usuário, estado atual:', showUserMenu);
                          setShowUserMenu(!showUserMenu);
                        }}
                        className="flex items-center space-x-2 px-2 py-1 h-auto hover:bg-gray-100"
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-blue-600 text-white text-sm">
                            {getUserInitials(user?.nome)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="hidden md:block text-left">
                          <div className="text-sm font-medium text-gray-900">{user?.nome}</div>
                          <div className="text-xs text-gray-500">{user?.cargo}</div>
                        </div>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('common.userMenu')}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* Dropdown menu */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-40">
                    <div className="py-1">
                      <div className="px-4 py-2 border-b border-gray-200">
                        <div className="text-sm font-medium text-gray-900">{user?.nome}</div>
                        <div className="text-sm text-gray-500">{user?.email}</div>
                      </div>
                      
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Navegando para usuários...');
                          navigate('/admin/users');
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center transition-colors duration-150"
                      >
                        <UserCog className="h-4 w-4 mr-3" />
                        {t('navigation.manageUsers')}
                      </button>
                      
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Executando logout...');
                          handleLogout();
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center transition-colors duration-150"
                      >
                        <LogOut className="h-4 w-4 mr-3" />
                        {t('common.logout')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Dropdown menu de Configurações - fora do header */}
        {showSettingsMenu && (
          <>
            <div 
              className="fixed inset-0 z-[50]" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowSettingsMenu(false);
              }}
            />
            <div 
              className="fixed right-4 top-14 w-56 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-[60]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="py-1">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate('/admin/configuracoes/rently');
                    setShowSettingsMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center transition-colors duration-150"
                >
                  <Settings className="h-4 w-4 mr-3" />
                  Integração Rently
                </button>
              </div>
            </div>
          </>
        )}

        {/* Overlay para fechar menu do usuário */}
        {showUserMenu && (
          <div 
            className="fixed inset-0 z-30" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Clicou no overlay, fechando menu');
              setShowUserMenu(false);
            }}
          />
        )}

        {/* Conteúdo principal */}
        <div className="pt-0 pr-6 pb-6 pl-0 md:pt-0 md:pr-8 md:pb-8 md:pl-0">
          <main className="bg-[#FFFFFF] rounded-[16px] p-6 md:p-8 min-h-[calc(100vh-64px-4rem)]">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
